function ImpactMetrics() {
    try {
        const [loading, setLoading] = React.useState(true);
        const [error, setError] = React.useState(null);
        
        const metrics = [
            {
                category: "Environmental Impact",
                metrics: [
                    {
                        label: "E-waste Diverted",
                        value: "2",
                        unit: "tons",
                        icon: "fa-recycle"
                    },
                    {
                        label: "CO₂ Emissions Saved",
                        value: "160",
                        unit: "kg",
                        icon: "fa-leaf"
                    },
                    {
                        label: "Trees Equivalent",
                        value: "8",
                        unit: "trees",
                        icon: "fa-tree"
                    }
                ]
            },
            {
                category: "Educational Impact",
                metrics: [
                    {
                        label: "Students Reached",
                        value: "5,000",
                        unit: "+",
                        icon: "fa-graduation-cap"
                    },
                    {
                        label: "Schools Supported",
                        value: "15",
                        unit: "",
                        icon: "fa-school"
                    },
                    {
                        label: "Training Hours",
                        value: "1,200",
                        unit: "hrs",
                        icon: "fa-clock"
                    }
                ]
            },
            {
                category: "Technical Impact",
                metrics: [
                    {
                        label: "Computing Power",
                        value: "10",
                        unit: "PFLOPS",
                        icon: "fa-microchip"
                    },
                    {
                        label: "Renewable Energy",
                        value: "1",
                        unit: "MW",
                        icon: "fa-solar-panel"
                    },
                    {
                        label: "Network Nodes",
                        value: "500",
                        unit: "",
                        icon: "fa-server"
                    }
                ]
            }
        ];

        React.useEffect(() => {
            let cleanupFunctions = [];
            
            // Initialize 3D visualizations when component mounts
            async function initVisualizations() {
                try {
                    setLoading(true);
                    
                    // Import the utility functions dynamically
                    const { createImpactVoxels } = await import('../../utils/threeScene.js');
                    
                    // Give DOM time to be fully rendered
                    setTimeout(async () => {
                        // Set up the voxel visualizations for each category
                        for (let index = 0; index < metrics.length; index++) {
                            const category = metrics[index];
                            const containerId = `voxel-container-${index}`;
                            
                            try {
                                const container = document.getElementById(containerId);
                                if (container) {
                                    const cleanup = await createImpactVoxels(containerId, category.metrics);
                                    if (cleanup) cleanupFunctions.push(cleanup);
                                }
                            } catch (vizError) {
                                console.error(`Error creating visualization ${index}:`, vizError);
                                // Continue with other visualizations even if one fails
                            }
                        }
                        
                        setLoading(false);
                    }, 100);
                } catch (error) {
                    console.error('Failed to initialize visualizations:', error);
                    setError(error.message);
                    setLoading(false);
                }
            }
            
            initVisualizations();
            
            // Clean up all visualizations when component unmounts
            return () => {
                cleanupFunctions.forEach(cleanup => {
                    try {
                        if (typeof cleanup === 'function') {
                            cleanup();
                        }
                    } catch (error) {
                        console.warn('Error in cleanup function:', error);
                    }
                });
            };
        }, []);

        return (
            <section className="py-24 bg-gray-800">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center mb-16 text-white">
                        Impact <span className="text-green-400">by the Numbers</span>
                    </h2>
                    <div className="space-y-24">
                        {metrics.map((category, index) => (
                            <div key={index} className="bg-gray-900/50 rounded-xl p-8 backdrop-blur-sm">
                                <h3 className="text-2xl font-bold mb-6 text-center text-white">
                                    {category.category}
                                </h3>
                                
                                {/* 3D Voxel Visualization Container */}
                                <div 
                                    id={`voxel-container-${index}`}
                                    className="w-full h-64 mb-8 rounded-lg overflow-hidden relative"
                                    aria-label={`3D visualization of ${category.category} metrics`}
                                >
                                    {loading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
                                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 text-center p-4">
                                            <div>
                                                <p className="text-yellow-400 mb-2">Visualization unavailable</p>
                                                <p className="text-sm text-gray-400">Using standard view instead</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    {category.metrics.map((metric, i) => (
                                        <div 
                                            key={i}
                                            className="voxel-metric-card bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-green-500 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300 transform hover:-translate-y-1"
                                        >
                                            <div className="flex items-center mb-4">
                                                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                                    <i className={`fas ${metric.icon} text-green-400 text-xl`}></i>
                                                </div>
                                                <div>
                                                    <div className="text-3xl font-bold text-white">
                                                        {metric.value}
                                                        <span className="text-green-400">{metric.unit}</span>
                                                    </div>
                                                    <div className="text-gray-400">{metric.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('ImpactMetrics error:', error);
        if (typeof reportError === 'function') {
            reportError(error);
        }
        
        // Fallback UI when the component fails to render
        return (
            <section className="py-24 bg-gray-800">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-12 text-white">
                        Impact <span className="text-green-400">by the Numbers</span>
                    </h2>
                    <div className="bg-gray-900/50 p-8 rounded-xl max-w-lg mx-auto">
                        <i className="fas fa-exclamation-triangle text-yellow-500 text-3xl mb-4"></i>
                        <p className="text-white text-xl mb-2">Visualization temporarily unavailable</p>
                        <p className="text-gray-400">Please check back later to see our impact metrics</p>
                    </div>
                </div>
            </section>
        );
    }
}

window.ImpactMetrics = ImpactMetrics;

export default ImpactMetrics;
